import answerTypesData from './data/answerTypes.json';

export const TRIVIA_DIFFICULTIES = ['easy', 'medium', 'hard'];
export const TRIVIA_REVIEW_STATUSES = ['draft', 'approved', 'disabled'];
export const TRIVIA_ANSWERS_PER_QUESTION = 4;
export const TRIVIA_ANSWER_TYPES = answerTypesData.answerTypes;

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function makeIssue(code, message, id = null) {
  return { code, message, id };
}

export function validateTriviaQuestionData(questionData, { requirePublicReady = false } = {}) {
  const issues = [];
  const categories = Array.isArray(questionData?.categories) ? questionData.categories : [];
  const questions = Array.isArray(questionData?.questions) ? questionData.questions : [];
  const categoryIds = new Set();
  const questionIds = new Set();
  const stems = new Set();

  if (questionData?.version !== 1) {
    issues.push(makeIssue('invalid-version', 'Question data must use schema version 1.'));
  }

  categories.forEach((category) => {
    if (!hasText(category?.id) || !hasText(category?.label) || !hasText(category?.description)) {
      issues.push(makeIssue('invalid-category', 'Categories require id, label, and description.', category?.id));
      return;
    }

    if (categoryIds.has(category.id)) {
      issues.push(makeIssue('duplicate-category', `Duplicate category id: ${category.id}`, category.id));
    }

    categoryIds.add(category.id);
  });

  questions.forEach((question) => {
    if (!hasText(question?.id)) {
      issues.push(makeIssue('missing-id', 'Questions require a stable id.'));
    } else if (questionIds.has(question.id)) {
      issues.push(makeIssue('duplicate-id', `Duplicate question id: ${question.id}`, question.id));
    } else {
      questionIds.add(question.id);
    }

    if (!categoryIds.has(question?.category)) {
      issues.push(makeIssue('unknown-category', `Unknown category: ${question?.category}`, question?.id));
    }

    if (!TRIVIA_DIFFICULTIES.includes(question?.difficulty)) {
      issues.push(makeIssue('invalid-difficulty', `Invalid difficulty: ${question?.difficulty}`, question?.id));
    }

    if (!TRIVIA_REVIEW_STATUSES.includes(question?.reviewStatus)) {
      issues.push(makeIssue('invalid-review-status', `Invalid review status: ${question?.reviewStatus}`, question?.id));
    }

    if (!TRIVIA_ANSWER_TYPES.includes(question?.answerType)) {
      issues.push(makeIssue('invalid-answer-type', `Invalid answer type: ${question?.answerType}`, question?.id));
    }

    if (requirePublicReady && question?.reviewStatus !== 'approved') {
      issues.push(makeIssue('non-approved-public-question', 'Public question data may only contain approved questions.', question?.id));
    }

    if (!hasText(question?.question)) {
      issues.push(makeIssue('missing-question', 'Question text is required.', question?.id));
    } else {
      const normalizedStem = question.question.trim().toLocaleLowerCase();

      if (stems.has(normalizedStem)) {
        issues.push(makeIssue('duplicate-stem', `Duplicate question stem: ${question.question}`, question.id));
      }

      stems.add(normalizedStem);
    }

    if (!Array.isArray(question?.answers) || question.answers.length !== TRIVIA_ANSWERS_PER_QUESTION) {
      issues.push(makeIssue('invalid-answer-count', 'Questions must have exactly four answers.', question?.id));
    } else {
      const answerIds = new Set();
      const correctAnswers = question.answers.filter((answer) => answer?.correct === true);

      if (correctAnswers.length !== 1) {
        issues.push(makeIssue('invalid-correct-count', 'Questions must have one and only one correct answer.', question?.id));
      }

      question.answers.forEach((answer) => {
        if (!hasText(answer?.id) || !hasText(answer?.text)) {
          issues.push(makeIssue('invalid-answer', 'Answers require id and text.', question?.id));
          return;
        }

        if (answer.answerType !== question?.answerType) {
          issues.push(makeIssue('answer-type-mismatch', `Answer type ${answer.answerType} does not match question type ${question?.answerType}.`, question.id));
        }

        if (answerIds.has(answer.id)) {
          issues.push(makeIssue('duplicate-answer-id', `Duplicate answer id: ${answer.id}`, question.id));
        }

        answerIds.add(answer.id);
      });
    }

    if (!hasText(question?.source?.name) || !hasText(question?.source?.url) || !hasText(question?.source?.license)) {
      issues.push(makeIssue('missing-source', 'Questions require source name, url, and license.', question?.id));
    }
  });

  return {
    valid: issues.length === 0,
    issues,
  };
}

export function assertValidTriviaQuestionData(questionData, options) {
  const result = validateTriviaQuestionData(questionData, options);

  if (!result.valid) {
    const detail = result.issues.map((issue) => `${issue.code}${issue.id ? `:${issue.id}` : ''}`).join(', ');
    throw new Error(`Invalid trivia question data: ${detail}`);
  }

  return questionData;
}
